import Graphic from "@arcgis/core/Graphic";
import { useEffect } from "react";
import { highlightFeature } from "./arcgisUtils";
import { metersPerNauticalMile } from "./constants";
import type FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import type { GeometryUnion } from "@arcgis/core/geometry/types";
import type { ResourceHandle } from "@arcgis/core/core/Handles";
import type Geometry from "@arcgis/core/geometry/Geometry";
import type Feature from "@arcgis/core/widgets/Feature";

/**
 * useClickedOutside Hook
 * @usage : useClickedOutside(ref, () => setIsOpen(false));
 * @description : Custom hook used for components to handle when user clicked outside the ref parameter.
 * @parameters : ref is a reference to the JSX element of the component.
 * onClickOutside is the function we want to call when clicked outside given component.
 */
export function useClickedOutside(ref: React.RefObject<HTMLDivElement | null>, onClickOutside: () => void) {
    useEffect(() => {
        function handleClickedOutside(event: MouseEvent): void {
            // trigger the provided function if this element was not clicked
            if (ref.current && !ref.current.contains(event.target as Node)) {
                onClickOutside();
            }
        }
        document.addEventListener("mousedown", handleClickedOutside);
        return () => {
            // clean up
            document.removeEventListener("mousedown", handleClickedOutside);
        };
    });
}

export const uuidv4 = () => {
    const str = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0,
            v = c == "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
    return `{${str.toUpperCase()}}`;
};

/**
 * Takes and object and optional zoom level to zoom to the object on the map.
 * @param object
 * @param zoomLevel
 * @returns
 */
export const zoomToObject = async (
    object: Graphic | Feature | Geometry,
    scale?: number,
    zoomLevel?: number,
    highlight?: boolean,
    highlightName?: string
): Promise<ResourceHandle | undefined> => {
    const map = document.getElementsByTagName("arcgis-map")[0];
    if (!map) {
        return;
    }

    let highlightHandle;
    await map.view
        .goTo(
            {
                target: object as Graphic | GeometryUnion,
                scale: scale,
                zoom: zoomLevel,
            },
            {
                animate: true,
                duration: 1000,
                easing: "ease-in-out",
            }
        )
        .then(async () => {
            if (object instanceof Graphic) {
                if (highlight) {
                    highlightHandle = await highlightFeature(object, highlightName);
                }
            }
        });
    return highlightHandle;
};

export const formatDate = (timestamp: number, withTime?: boolean) => {
    if (!timestamp) {
        return "";
    }
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const time = withTime ? ` ${hours}:${minutes}` : "";
    return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}${time}`;
};

export const formatDateToISO = (timestamp: number) => {
    if (!timestamp) {
        return "";
    }
    const date = new Date(timestamp);
    return date.toISOString().split("T")[0];
};

export const isNumber = (value: string) => {
    return !Number.isNaN(Number(value));
};

export const formatScaleString = (scale: number) => {
    return `1:${scale.toFixed(0)}`;
};

export const parseScaleString = (scale: string) => {
    return Number.parseInt(scale.replace("1:", ""));
};

export const metersToNauticalMiles = (meters: string) => {
    const parts = meters.split(" ");
    const unit = parts[parts.length - 1];

    meters = meters.replaceAll(/[a-zA-Z\s]/g, "");
    const metersNumber = Number.parseFloat(meters);
    if (Number.isNaN(metersNumber)) {
        throw new Error("Invalid input: meters must be a number.");
    }

    if (unit === "Nm") {
        return meters;
    }
    if (unit === "m") {
        return (metersNumber / metersPerNauticalMile).toFixed(4).replace(".", ",") + " Nm";
    }
    if (unit === "km") {
        return ((metersNumber * 1000) / metersPerNauticalMile).toFixed(4).replace(".", ",") + " Nm";
    }
};

export const nauticalMilesToMeters = (nauticalMiles: string) => {
    const parts = nauticalMiles.split(" ");
    const length = parts[0];

    const nauticalMilesNumber = Number.parseFloat(length.replace(",", "."));
    if (Number.isNaN(nauticalMilesNumber)) {
        throw new Error("Invalid input: nautical miles must be a number.");
    }

    return nauticalMilesNumber * metersPerNauticalMile + " m";
};

export const clamp = (value: number, min: number = 0, max: number = Infinity, tolerance: number = 0.001) => {
    return Math.min(Math.max(value, min + tolerance), max - tolerance);
};

export const clampWithCircleRollover = (value: number, min: number, max: number, tolerance: number = 0.001) => {
    // Value should be between 0 and max, OR between min and 360
    // const center = (min - max) / 2;
    if (value < min) {
        return clamp(value, 0 + tolerance, max - tolerance);
    } else {
        return clamp(value, min + tolerance, 360 - tolerance);
    }
};

export const useNumberOfDigits = (value: number | string, digits: number = 2) => {
    if (typeof value === "string") {
        value = Number.parseFloat(value);
    }
    return Number.parseFloat(value.toFixed(digits));
};

export const deepCopy = (obj: any) => {
    return JSON.parse(JSON.stringify(obj));
};

export const hasAccessToGroups = (self: any, groupIds: string[]) => {
    if (!self || !self.groups) {
        return false;
    }
    return self.groups.some((group: any) => groupIds.includes(group.id));
};

export const hasAccessToRoles = (self: any, roles: string[]) => {
    if (!self || !self.roleId) {
        return false;
    }
    return roles.includes(self.roleId);
};

// Function to obscure the string
export const obscureString = (inputString: string) => {
    const XOR_KEY = 42; // Define the XOR key
    // First layer: Reverse the string
    const reversedString = inputString.split("").reverse().join("");

    // Second layer: Base64 encode
    const base64Encoded = btoa(reversedString);

    // Third layer: XOR each character with the key
    const xorEncoded = [...base64Encoded].map((char) => String.fromCharCode(char.charCodeAt(0) ^ XOR_KEY)).join("");

    // URL encode the result
    const urlEncoded = encodeURIComponent(xorEncoded);

    return urlEncoded;
};

// Function to deobscure the string
export const deobscureString = (obscuredString: string) => {
    const XOR_KEY = 42; // Define the XOR key
    // URL decode the string
    const urlDecoded = decodeURIComponent(obscuredString);

    // Reverse the XOR operation
    const xorDecoded = [...urlDecoded].map((char) => String.fromCharCode(char.charCodeAt(0) ^ XOR_KEY)).join("");

    // Decode Base64
    const base64Decoded = atob(xorDecoded);

    // Reverse the string to get the original
    const originalString = base64Decoded.split("").reverse().join("");

    return originalString;
};

export const convertBlobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64String = reader.result as string;
            resolve(base64String);
        };
        reader.onerror = () => {
            reject(new Error("Failed to convert blob to base64"));
        };
        reader.readAsDataURL(blob);
    });
};

export const groupBy = (objectToGroup: any[], key: string) => {
    // Using reduce for grouping
    const objectGrouped = objectToGroup.reduce((acc, item) => {
        const keyValue = item[key];
        // Still need to check if the key exists
        if (!acc[keyValue]) acc[keyValue] = [];

        acc[keyValue].push(item);

        return acc;
    }, {});

    return objectGrouped;
};

/**
 * Dispatches a custom event to refresh a feature
 * @param layerId - The ID of the layer containing the feature
 * @param objectId - The object ID of the feature to refresh
 * @param shouldSearchItemRefresh - Whether to refresh the search item only a few attributes require searchItem to refresh, to avoid unnecessary refreshes when attributes that are not shown in searchItem are updated
 */
export const dispatchRefreshFeature = (layerId: string, objectId: number, shouldSearchItemRefresh: boolean) => {
    const event = new CustomEvent("refreshFeature", {
        detail: { layerId, objectId, shouldSearchItemRefresh },
    });
    globalThis.dispatchEvent(event);
};

/**
 * Listens for refreshFeature events and executes the provided callback
 * @param callback - Function to execute when the event is triggered
 * @returns A function to remove the event listener
 */
export const onRefreshFeature = (
    callback: (layerId: string, objectId: number, shouldSearchItemRefresh: boolean) => void
) => {
    const handler = (event: Event) => {
        const customEvent = event as CustomEvent<{
            layerId: string;
            objectId: number;
            shouldSearchItemRefresh: boolean;
        }>;
        callback(customEvent.detail.layerId, customEvent.detail.objectId, customEvent.detail.shouldSearchItemRefresh);
    };

    globalThis.addEventListener("refreshFeature", handler);

    // Return cleanup function to remove the listener
    return () => {
        globalThis.removeEventListener("refreshFeature", handler);
    };
};

export const layerHasField = (layer: FeatureLayer, fieldName: string): boolean => {
    if (!layer.fields) {
        return false;
    }
    const hasField = layer.fields.some((field) => field.name === fieldName);
    return hasField;
};
